import React from 'react';
import { Link } from 'react-router-dom';
import GuideLayout, { InfoBox, SectionCTA } from '../../components/GuideLayout';

const s = {
  breadcrumb: { fontSize: 13, color: '#94a3b8', marginBottom: 20 },
  breadcrumbLink: { color: '#2563eb', textDecoration: 'none' },
  h1: { fontSize: 32, fontWeight: 800, color: '#0f172a', lineHeight: 1.25, marginBottom: 16, letterSpacing: '-0.4px' },
  lead: { fontSize: 17, color: '#475569', lineHeight: 1.7, marginBottom: 32, borderBottom: '1px solid #e2e8f0', paddingBottom: 32 },
  h2: { fontSize: 22, fontWeight: 700, color: '#0f172a', marginTop: 36, marginBottom: 14, letterSpacing: '-0.2px' },
  h3: { fontSize: 17, fontWeight: 600, color: '#1e293b', marginTop: 24, marginBottom: 10 },
  p: { fontSize: 15, color: '#374151', lineHeight: 1.75, marginBottom: 16 },
  ul: { paddingLeft: 20, marginBottom: 16 },
  li: { fontSize: 15, color: '#374151', lineHeight: 1.75, marginBottom: 6 },
  mythGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 24 },
  mythGridMobile: { display: 'grid', gridTemplateColumns: '1fr', gap: 12, marginBottom: 24 },
  myth: { background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '14px 16px' },
  reality: { background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '14px 16px' },
  mythLabel: { fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 },
  mythText: { fontSize: 14, color: '#374151', lineHeight: 1.5 },
  nav: { display: 'flex', justifyContent: 'space-between', marginTop: 48, paddingTop: 24, borderTop: '1px solid #e2e8f0' },
  prevLink: { fontSize: 14, color: '#2563eb', textDecoration: 'none', fontWeight: 600 },
  nextLink: { fontSize: 14, color: '#2563eb', textDecoration: 'none', fontWeight: 600 },
};

const myths = [
  { myth: 'You need to be a large company', reality: 'SMEs win a significant share of public contracts. Many tenders are specifically designed for smaller businesses.' },
  { myth: 'You need years of public sector experience', reality: 'First-time bidders win contracts regularly. Strong technical proposals can outweigh limited track record.' },
  { myth: 'It is only for local companies', reality: 'EU law gives every business in the EU the right to bid for any above-threshold contract across all member states.' },
  { myth: 'You need existing relationships with the buyer', reality: 'Procurement rules exist specifically to prevent favouritism. Evaluation is based on your written submission, not who you know.' },
];

export default function WhoCanBid() {
  return (
    <GuideLayout>
      <div style={s.breadcrumb}>
        <Link to="/guide" style={s.breadcrumbLink}>Tender Guide</Link> &rsaquo; Who can bid?
      </div>

      <h1 style={s.h1}>Who can bid for public contracts?</h1>
      <p style={s.lead}>
        In principle, any legally registered business can bid for a public tender — a sole trader, a startup, an SME, or a multinational. There is no mandatory size requirement. The rules exist to ensure the market is open and competitive.
      </p>

      <h2 style={s.h2}>Common myths — and the reality</h2>
      <div style={s.mythGrid}>
        {myths.map((m, i) => (
          <React.Fragment key={i}>
            <div style={s.myth}>
              <div style={{ ...s.mythLabel, color: '#b91c1c' }}>Myth</div>
              <div style={s.mythText}>{m.myth}</div>
            </div>
            <div style={s.reality}>
              <div style={{ ...s.mythLabel, color: '#15803d' }}>Reality</div>
              <div style={s.mythText}>{m.reality}</div>
            </div>
          </React.Fragment>
        ))}
      </div>

      <h2 style={s.h2}>Eligibility requirements</h2>
      <p style={s.p}>
        Most tenders require you to demonstrate that your business is in good standing. This typically means:
      </p>
      <ul style={s.ul}>
        <li style={s.li}><strong>Legal registration</strong> — your company must be properly incorporated or registered as a trader in your country</li>
        <li style={s.li}><strong>Financial standing</strong> — buyers often set a minimum annual turnover (commonly 1&times; to 2&times; the contract value) and may ask for audited accounts</li>
        <li style={s.li}><strong>Professional liability insurance</strong> — most service contracts require evidence of cover, typically &euro;1M&ndash;&euro;5M depending on the work</li>
        <li style={s.li}><strong>Relevant experience</strong> — case studies or references demonstrating you have done similar work before</li>
        <li style={s.li}><strong>Technical capability</strong> — staff CVs, accreditations, certifications relevant to the contract (e.g. ISO 27001 for IT security work)</li>
      </ul>

      <InfoBox>
        <strong>Tip:</strong> Requirements vary widely by contract. A &euro;50,000 training contract will ask for far less documentation than a &euro;2M cloud infrastructure programme. Always read the qualification criteria before deciding whether to bid.
      </InfoBox>

      <h2 style={s.h2}>Grounds for exclusion</h2>
      <p style={s.p}>
        EU procurement law sets out mandatory and discretionary grounds on which a buyer must or may exclude a bidder. Mandatory exclusions include:
      </p>
      <ul style={s.ul}>
        <li style={s.li}>Conviction for corruption, fraud, money laundering, or organised crime</li>
        <li style={s.li}>Non-payment of taxes or social security contributions</li>
        <li style={s.li}>Insolvency or bankruptcy proceedings</li>
      </ul>
      <p style={s.p}>
        Discretionary exclusions (where the buyer may, but is not required to, exclude) include significant professional misconduct, past poor contract performance, or misrepresentation in a previous bid.
      </p>
      <p style={s.p}>
        In practice, most businesses in good standing with no criminal history are not affected by these rules. You will be asked to self-declare compliance, typically using a standard European Single Procurement Document (ESPD).
      </p>

      <h2 style={s.h2}>Bidding as a consortium</h2>
      <p style={s.p}>
        You do not have to bid alone. Consortium bids — where two or more companies submit a joint proposal — are explicitly permitted under EU rules. This is useful when:
      </p>
      <ul style={s.ul}>
        <li style={s.li}>The contract requires a breadth of capability that no single company covers alone</li>
        <li style={s.li}>The financial turnover requirement is above what one company can demonstrate individually</li>
        <li style={s.li}>You want to enter a new market by partnering with an established local operator</li>
      </ul>
      <p style={s.p}>
        One consortium member acts as the lead bidder and takes legal responsibility for the submission. Subcontracting arrangements (where you bring in specialists but remain sole bidder) are also common and similarly permitted.
      </p>

      <h2 style={s.h2}>Cross-border bidding rights</h2>
      <p style={s.p}>
        Any business registered in an EU member state has the legal right to bid for above-threshold public contracts in any other EU country. A German IT firm can bid for a Belgian government contract; a Dutch consultancy can respond to a Spanish ministry tender. This is a core principle of the EU single market.
      </p>
      <p style={s.p}>
        In practice, language and local market knowledge remain barriers, but they are practical rather than legal ones. Many above-threshold tenders accept proposals in English or provide English-language notices.
      </p>

      <SectionCTA />

      <div style={s.nav}>
        <Link to="/guide/what-is-a-tender" style={s.prevLink}>&larr; What is a public tender?</Link>
        <Link to="/guide/bidding-process" style={s.nextLink}>Next: The bidding process &rarr;</Link>
      </div>
    </GuideLayout>
  );
}
